import type { ApiError } from "@ignite/api"
import type { TokenResponse } from "@ignite/api/users"
import EpiConfig from "@ignite/config"
import jwt from "@ignite/services/jwt"
import {
  impersonatedUserId,
  IMPERSONATION_COOKIE,
  isImpersonating,
} from "@ignite/utils/impersonation"
import { isLocalHost } from "@ignite/utils/location"
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios"
import qs from "qs"

export const BASE_URL = (window["BASE_URL"] || process.env.REACT_APP_API_ROOT)!

interface AxiosApiError extends AxiosError {
  response: AxiosResponse<ApiError>
}

const request = axios.create({
  baseURL: BASE_URL,
  withCredentials: BASE_URL.endsWith("apiary-mock.com/") ? false : true,
})

export const requestWithoutTokenUpdate = axios.create({
  baseURL: BASE_URL,
  withCredentials: BASE_URL.endsWith("apiary-mock.com/") ? false : true,
})

const requestInterceptor = async (
  useTokenUpdate: boolean,
  config: AxiosRequestConfig
) => {
  if (
    config.data &&
    config.headers!["Content-Type"] === "application/x-www-form-urlencoded"
  ) {
    config.data = qs.stringify(config.data)
  }

  if (jwt.token) {
    if (useTokenUpdate && Date.now() > jwt.tokenExpires) {
      await jwt.updateToken()
    }
    config.headers!.Authorization = `Bearer ${jwt.token}`
  }

  //TODO check error with @types
  config.headers!["Accept-Language"] = window["LANGUAGE_CODE"] || "en"

  config.headers!["X-Requested-With"] = "XMLHttpRequest" // Detecting XMLHttprequest from javascript, default disabled in axios

  if (isLocalHost() && isImpersonating()) {
    config.headers![IMPERSONATION_COOKIE] = impersonatedUserId() || ""
  }

  return config
}

requestWithoutTokenUpdate.interceptors.request.use((config) =>
  requestInterceptor(false, config)
)

request.interceptors.request.use((config) => requestInterceptor(true, config))

const responseInterceptorResponse = (response: AxiosResponse<any>) => {
  if (isTokenResponse(response.data)) {
    jwt.token = response.data.access_token
    jwt.tokenExpires = response.data.expires_in
    jwt.refreshToken = response.data.refresh_token
  }
  return response.data
}

const responseInterceptorError = async (error: any) => {
  if (axios.isCancel(error)) {
    return Promise.reject(error)
  }

  if (!isApiError(error)) {
    return Promise.reject(
      EpiConfig.alwaysReturnApiErrors === "true"
        ? error.response.data
        : "NETWORK_ERROR"
    )
  }

  if (isExpiredRefreshTokenError(error)) {
    jwt.clear() // We clear our tokens
    window.location.reload() // And reload the page to make sure user is logged out
    return Promise.reject(error.response.data.code)
  }

  if (isExpiredTokenError(error) && !error.config["isRetryRequest"]) {
    if (!jwt.refreshToken) {
      return Promise.reject(error.response.data.code)
    }

    await jwt.updateToken()

    error.config["isRetryRequest"] = true
    return request(error.config)
  }
  return Promise.reject(error.response.data)
}

request.interceptors.response.use(
  (response: AxiosResponse) => responseInterceptorResponse(response),
  async (error: any) => await responseInterceptorError(error)
)

requestWithoutTokenUpdate.interceptors.response.use(
  (response: AxiosResponse) => responseInterceptorResponse(response),
  async (error: any) => await responseInterceptorError(error)
)

const isExpiredRefreshTokenError = (error: AxiosApiError) =>
  error.response.status === 400 &&
  error.response.data.error === "invalid_grant" &&
  error.config.data?.toLowerCase().includes("grant_type=refresh_token")

const isTokenResponse = (arg: any): arg is TokenResponse =>
  arg.access_token !== undefined && arg.refresh_token !== undefined

const isApiError = (error: any): error is AxiosApiError =>
  error && error.response && error.response.data

const isExpiredTokenError = (error: AxiosApiError) =>
  error.config && error.response.status === 401

export default request
